import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { AccountPage } from "@common-components/account-page/account-page";
import BTButton from "@common-components/bt-button/bt-button";
import BTtextFieldWithLabel from "@common-components/bt-input-field-with-label/bt-input-field-with-label";
import ReusableModal from '@common-components/modals/reusable-modal';
import PersonAvatar from '@common-components/tasks/person-avatar/person-avatar';
import { MODAL, modalActions } from '@common-reducers/ModalReducer';
import { closePaymentDialogThunk } from '@common-reducers/PaymentFormThunks';
import { selectUserObject } from "@common-reducers/UserSelectors";
import { handleUpload } from "@common-services/upload";
import { namespace } from '@common/config/constants';
import { DBUserThunks } from '@common/reducers/DBServiceThunks';

import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { Alert, Snackbar, FormControl, MenuItem, Select, SelectChangeEvent, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import replaceImg from "@common-assets/icons/replaceImg.png";
import { User, UserInterface } from "@common/models/user";
import classes from "./account-view.module.scss";
import { setUser } from '@/common/reducers/UserReducer';
import LanguageSwitcher from '@common/components/language-switcher/language-switcher';
import BTTimePicker from '@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-time-picker';
import { changeUserLanguage, normalizeLanguageCode } from '@common/services/language-detection';

export interface AccountViewProps {
  readOnly?: boolean;
  /** Chromeless mode for the `/clear-account` route — see AccountPage's `bare`. */
  bare?: boolean;
}
export interface AccountViewState {
  user?: User | null | undefined;
}

export const AccountView = ({ readOnly, bare = false }: AccountViewProps) => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch()
  const user: UserInterface | undefined = useAppSelector(selectUserObject);


  const [isLoading, setIsLoading] = useState(false);

  const [inputValues, setInputValues] = useState({
    email: user?.email || '',
    phone: user?.phone || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });

  const [localCalendarStartDay, setLocalCalendarStartDay] = useState(user?.preferences?.calendarStartDay ?? 0);
  const [localPreferredLanguage, setLocalPreferredLanguage] = useState(() =>
    normalizeLanguageCode(user?.preferredLanguage || i18n.resolvedLanguage || i18n.language || 'en')
  );
  
  // Default scheduling time: "current time" or "HH:mm" format
  const [defaultSchedulingTimeType, setDefaultSchedulingTimeType] = useState<'current' | 'custom'>(() => {
    const stored = user?.preferences?.defaultSchedulingTime;
    return stored && stored !== 'current time' ? 'custom' : 'current';
  });
  const [customTime, setCustomTime] = useState<string>(() => {
    const stored = user?.preferences?.defaultSchedulingTime;
    if (stored && stored !== 'current time' && /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(stored)) {
      return stored;
    }
    // Default to current time in HH:mm format
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  });

  const [profileImgDataUrl, setProfileImgDataUrl] = useState<string | undefined>();
  const [profileImgType, setProfileImgType] = useState<string | undefined>();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackBarSeverity, setSnackBarSeverity] = useState<any>();
  const [snackbarMessage, setSnackbarMessage] = useState<string>();

  // Track mount state so async callbacks (FileReader / upload) don't setState after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    const file = event.target.files[0];
    if (file && file.type.indexOf('image') > -1) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (!isMountedRef.current) return;
        if (inputFilesRef.current) inputFilesRef.current.value = ''
        setProfileImgDataUrl(reader.result as string);
        setProfileImgType(file.type);
      };
      reader.readAsDataURL(file);
    } else {
      setProfileImgDataUrl('');
      setProfileImgType('');
    }
  }, []);

  const saveUser = useCallback(async () => {
    console.log('saveUser');
    setIsLoading(true);

    const fieldsToPatch: Partial<User> = { ...inputValues };
    const previousPreferredLanguage = normalizeLanguageCode(
      user?.preferredLanguage || i18n.resolvedLanguage || i18n.language || 'en'
    );
    const shouldUpdatePreferredLanguage = localPreferredLanguage !== previousPreferredLanguage;

    if (shouldUpdatePreferredLanguage) {
      fieldsToPatch.preferredLanguage = localPreferredLanguage;
    }

    if (profileImgDataUrl) {
      let urlLink = '';
      try {
        const response = await handleUpload(profileImgDataUrl, profileImgType ?? '');
        urlLink = response?.image_url ?? urlLink;
      } catch (error) {
        console.log('upload error', error);
      }
      console.log('uploaded', urlLink);
      if (urlLink) {
        fieldsToPatch.profileImg = urlLink;
      }
    }

    // Add preferences including defaultSchedulingTime
    const newSchedulingTime = defaultSchedulingTimeType === 'current' ? 'current time' : customTime;
    const currentSchedulingTime = user?.preferences?.defaultSchedulingTime;
    
    // If field doesn't exist in DB, create it with the current value from UI
    // If it exists, update it only if there's a change
    let schedulingTimeToSave: string | undefined;
    if (currentSchedulingTime === undefined) {
      // Field doesn't exist - create it with the current value from UI
      schedulingTimeToSave = newSchedulingTime;
    } else if (currentSchedulingTime !== newSchedulingTime) {
      // Field exists and value changed - update it
      schedulingTimeToSave = newSchedulingTime;
    }
    // If field exists and value didn't change, don't include it in patch
    
    fieldsToPatch.preferences = {
      ...user?.preferences,
      calendarStartDay: localCalendarStartDay,
      ...(schedulingTimeToSave !== undefined && { defaultSchedulingTime: schedulingTimeToSave }),
    };

    const res: any = await dispatch(DBUserThunks.patch({
      entity: {
        _id: user?._id,
        ...fieldsToPatch,
      }
    }))

    // Bail out if the component unmounted while the patch was in flight
    if (!isMountedRef.current) return;

    // DBUserThunks.patch FULFILS with the server's error body when the patch fails (it does not
    // reject), so `res.error` is unset on a failed save. Only a returned user doc is a success;
    // anything else must neither replace the user object (that emptied the sidebar) nor say "updated".
    const saved = res?.payload && typeof res.payload === 'object' && res.payload._id === user?._id;
    if (saved) {
      dispatch(setUser(res.payload));
    }

    if (res?.error || !saved) {
      setSnackBarSeverity('error');
      setSnackbarMessage(t('accountView.snackbarErrorMessage'));
      setOpenSnackbar(true);
      setIsLoading(false);
    } else {
      if (shouldUpdatePreferredLanguage) {
        await changeUserLanguage(localPreferredLanguage, undefined, dispatch);
      }
      if (!isMountedRef.current) return;
      setSnackbarMessage(t('accountView.snackbarSuccessMessage'));
      setSnackBarSeverity('success');
      setProfileImgDataUrl(undefined);
      setProfileImgType('');
      setOpenSnackbar(true);
    }
    setIsLoading(false);
  }, [dispatch, inputValues, profileImgDataUrl, profileImgType, user?._id, user?.preferredLanguage, i18n.resolvedLanguage, i18n.language, t, defaultSchedulingTimeType, customTime, localCalendarStartDay, user?.preferences, localPreferredLanguage]);

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  }, [inputValues]);

  const inputFilesRef = useRef<HTMLInputElement>(null);


  useEffect(() => {
    document.title = t('accountView.pageTitle');

    dispatch(closePaymentDialogThunk());

  }, []);

  // Sync local calendar start day with user preferences when user object changes
  useEffect(() => {
    if (user?.preferences?.calendarStartDay !== undefined) {
      setLocalCalendarStartDay(user.preferences.calendarStartDay);
    }
  }, [user?.preferences?.calendarStartDay]);

  // Sync default scheduling time with user preferences when user object changes
  useEffect(() => {
    const stored = user?.preferences?.defaultSchedulingTime;
    if (stored) {
      if (stored === 'current time') {
        setDefaultSchedulingTimeType('current');
      } else if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(stored)) {
        setDefaultSchedulingTimeType('custom');
        setCustomTime(stored);
      }
    } else {
      // If no value exists, default to "current"
      setDefaultSchedulingTimeType('current');
    }
  }, [user?.preferences?.defaultSchedulingTime]);

  useEffect(() => {
    const nextLanguage = normalizeLanguageCode(
      user?.preferredLanguage || i18n.resolvedLanguage || i18n.language || 'en'
    );
    setLocalPreferredLanguage(nextLanguage);
  }, [user?.preferredLanguage, i18n.resolvedLanguage, i18n.language]);

  const handleStartDayChange = (event: SelectChangeEvent<number>) => {
    const newStartDay = event.target.value as number;
    // Update local state immediately for instant UI feedback
    setLocalCalendarStartDay(newStartDay);
    if (!user?._id) return;
    dispatch(DBUserThunks.patch({
      entity: {
        _id: user._id,
        preferences: {
          ...user?.preferences,
          calendarStartDay: newStartDay
        }
      }
    }));
  };

  const handleSchedulingTimeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as 'current' | 'custom';
    setDefaultSchedulingTimeType(newType);
    // Changes will be saved only when user clicks "Save Changes"
  };

  const handleCustomTimeChange = (time: string) => {
    setCustomTime(time);
    // Changes will be saved only when user clicks "Save Changes"
  };

  const accountView = () => {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.titleContainer}>
          <h4 className={classes.pageTitle}>{t('accountView.pageTitle')}</h4>
        </div>
        <div className={classes.accountSettingBody}>
          {/* Simplified, clean layout */}
          <div className="max-w-2xl">
            {/* Profile Image - no label needed */}
            <div className="mb-10">
              <div style={{ position: "relative", maxWidth: "150px" }}>
                <PersonAvatar size={150} email={user?.email}
                  profileImg={profileImgDataUrl ?? user?.profileImg} lastName={user?.lastName}
                  firstName={user?.firstName} textSize={60} />

                <label htmlFor="profileImg">
                  <img
                    style={{ cursor: "pointer" }}
                    className={classes.replaceImg}
                    src={replaceImg}
                    width={32}
                    height={32}
                    alt=""
                  />
                </label>

                <input
                  disabled={isLoading || readOnly}
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={inputFilesRef}
                  type="file"
                  id="profileImg"
                  onChange={onFileChange}
                />
              </div>
            </div>

            {/* Form Fields - clean and simple */}
            <div className="max-w-lg">
              <Stack spacing={3}>
              <BTtextFieldWithLabel
                label={t('accountView.firstNameLabel')}
                placeholder={t('accountView.firstNamePlaceholder')}
                disabled={readOnly}
                onChange={handleChangeInput}
                name="firstName"
                value={inputValues.firstName}
                sx={{
                  backgroundColor: 'var(--color-surface)',
                }}
              />
              <BTtextFieldWithLabel
                label={t('accountView.lastNameLabel')}
                placeholder={t('accountView.lastNamePlaceholder')}
                disabled={readOnly}
                onChange={handleChangeInput}
                name="lastName"
                value={inputValues.lastName}
                sx={{
                  backgroundColor: 'var(--color-surface)',
                }}
              />

              <BTtextFieldWithLabel
                label={t('accountView.emailLabel')}
                disabled
                placeholder={t('accountView.emailPlaceholder')}
                onChange={handleChangeInput}
                name="email"
                value={inputValues.email}
                sx={{
                  backgroundColor: 'var(--color-surface)',
                  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--color-border)',
                  },
      
                }}
              />
              <BTtextFieldWithLabel
                label={t('accountView.phoneLabel')}
                placeholder={t('accountView.phonePlaceholder')}
                disabled={readOnly}
                onChange={handleChangeInput}
                name="phone"
                value={inputValues.phone}
                sx={{
                  backgroundColor: 'var(--color-surface)',
                }}
              />

              {/* Preferences */}
              <div className="pt-2">
                <LanguageSwitcher
                  value={localPreferredLanguage}
                  onLanguageChange={setLocalPreferredLanguage}
                  applyImmediately={false}
                />
              </div>
              
              <FormControl
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'var(--color-primary)',
                    '& fieldset': {
                      borderColor: 'var(--color-border)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--color-primary)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--color-primary)',
                      borderWidth: '2px',
                    }
                  }
                }}
              >
                <span style={{ fontSize: "14px", color: 'var(--color-text)', fontWeight: 500, marginBottom: '8px', display: 'block' }}>{t('accountView.calendarStartDay')}</span>
                <Select
                  value={localCalendarStartDay}
                  onChange={handleStartDayChange}
                  displayEmpty
                  sx={{
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text)',
                    '& .MuiSelect-icon': {
                      color: 'var(--color-text)'
                    },
                    '& .MuiSelect-select': {
                      color: 'var(--color-text)'
                    }
                  }}
                >
                  <MenuItem value={0}>{t('accountView.startOnSunday')}</MenuItem>
                  <MenuItem value={1}>{t('accountView.startOnMonday')}</MenuItem>
                </Select>
              </FormControl>

              {/* Default Scheduling Time */}
              <div>
                <span style={{ fontSize: "14px", color: 'var(--color-text)', fontWeight: 500, marginBottom: '8px', display: 'block' }}>{t('accountView.defaultSchedulingTime')}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '32px 8px', flexWrap: 'wrap' }}>
                  <FormControl component="fieldset" disabled={readOnly}>
                    <RadioGroup
                      value={defaultSchedulingTimeType}
                      onChange={handleSchedulingTimeTypeChange}
                      row
                      sx={{
                        gap: '32px', // More space between radio buttons
                        '& .MuiRadio-root': {
                          color: 'var(--color-text)',
                          '&.Mui-checked': {
                            color: 'var(--color-primary)',
                          },
                          '&.Mui-disabled': {
                            color: 'var(--color-text-disabled, rgba(0, 0, 0, 0.38))',
                          }
                        },
                        '& .MuiFormControlLabel-label': {
                          color: 'var(--color-text)',
                          fontSize: '14px',
                          '&.Mui-disabled': {
                            color: 'var(--color-text-disabled, rgba(0, 0, 0, 0.38))',
                          }
                        }
                      }}
                    >
                      <FormControlLabel
                        value="current"
                        control={<Radio />}
                        label={t('accountView.currentTime')}
                      />
                      <FormControlLabel
                        value="custom"
                        control={<Radio />}
                        label={t('accountView.customTime')}
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Time Picker - always visible, disabled when current time is selected */}
                  <BTTimePicker
                    value={customTime}
                    onChange={handleCustomTimeChange}
                    disabled={defaultSchedulingTimeType === 'current' || readOnly}
                  />
                </div>
              </div>
              
              {/* Save Button */}
              {!readOnly && (
                <div style={{ paddingTop: '12px' }}>
                  <BTButton onClick={saveUser} loading={isLoading} >
                    {t('accountView.saveChangesButton')}
                  </BTButton>
                </div>
              )}
            </Stack>
            </div>

            {/* Delete Account - subtle and professional */}
            <div className="mt-12 pt-10 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">{t('accountView.deleteAccount')}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t('accountView.deleteAccountDescription')}
                  </p>
                </div>
                <Box
                  sx={{
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#dc2626",
                    border: "1px solid #e5e7eb",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    display: "inline-block",
                    transition: "all 0.2s ease",
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#fef2f2',
                      borderColor: '#fca5a5',
                    },
                    '.dark &': {
                      borderColor: '#374151',
                      '&:hover': {
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                        borderColor: '#dc2626',
                      }
                    }
                  }}
                  onClick={() => dispatch(modalActions.setComponentToRender(MODAL.DELETE_ACCOUNT))}
                >
                  {t('accountView.delete')}
                </Box>
              </div>
            </div>
            
            <ReusableModal />

            <Snackbar
              open={openSnackbar}
              autoHideDuration={6000}
              onClose={() => setOpenSnackbar(false)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <Alert onClose={() => setOpenSnackbar(false)} severity={snackBarSeverity} variant="filled" sx={{
                "& .MuiButtonBase-root": {
                  color: 'var(--color-surface);'
                }
              }}>
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AccountPage
      page={accountView()}
      readOnly={readOnly}
      bare={bare}
    ></AccountPage>
  );
};

export default AccountView;
